/**
 * Task UI Store - Zustand state management for task engine UI state
 * Holds ONLY ephemeral UI state: active filters, form visibility, collapsed sections.
 * Server data lives in React Query (useTasks.ts).
 */

import { create } from 'zustand';
import type { SchedulerViewMode, MyTasksSection, TaskStatus } from '../types/tasks';

interface TaskUIState {
  // Scheduler page
  schedulerViewMode: SchedulerViewMode;
  schedulerFormOpen: boolean;
  schedulerStatusFilter: TaskStatus | 'ALL';
  schedulerWorkflowFilter: string; // 'all' or specific workflowType
  schedulerAssigneeFilter: string; // 'all' or userId

  // My Tasks page
  myTasksFormOpen: boolean;
  myTasksWorkflowFilter: string;
  myTasksStatusFilter: TaskStatus | 'ALL';

  // Collapsible sections (My Tasks priority groups)
  expandedSections: Record<MyTasksSection, boolean>;
  // Collapsed workflow groups (Scheduler — tracked as collapsed, empty = all expanded)
  expandedWorkflows: Record<string, boolean>;
}

interface TaskUIActions {
  setSchedulerViewMode: (mode: SchedulerViewMode) => void;
  toggleSchedulerForm: () => void;
  setSchedulerStatusFilter: (status: TaskStatus | 'ALL') => void;
  setSchedulerWorkflowFilter: (filter: string) => void;
  setSchedulerAssigneeFilter: (filter: string) => void;
  toggleMyTasksForm: () => void;
  setMyTasksWorkflowFilter: (filter: string) => void;
  setMyTasksStatusFilter: (status: TaskStatus | 'ALL') => void;
  toggleSection: (section: MyTasksSection) => void;
  toggleWorkflow: (workflowInstanceId: string) => void;
  resetFilters: () => void;
}

type TaskUIStore = TaskUIState & TaskUIActions;

const initialState: TaskUIState = {
  schedulerViewMode: 'workflow',
  schedulerFormOpen: false,
  schedulerStatusFilter: 'ALL',
  schedulerWorkflowFilter: 'all',
  schedulerAssigneeFilter: 'all',

  myTasksFormOpen: false,
  myTasksWorkflowFilter: 'all',
  myTasksStatusFilter: 'ALL',

  expandedSections: {
    overdue: true,
    pinned: true,
    blocked: true,
    upcoming: true,
    available: true,
    completed: false,
    watching: true,
  },

  expandedWorkflows: {},
};

export const useTaskUIStore = create<TaskUIStore>()((set) => ({
  ...initialState,

  setSchedulerViewMode: (mode) => {
    set({ schedulerViewMode: mode });
  },

  toggleSchedulerForm: () => {
    set((state) => ({ schedulerFormOpen: !state.schedulerFormOpen }));
  },

  setSchedulerStatusFilter: (status) => {
    set({ schedulerStatusFilter: status });
  },

  setSchedulerWorkflowFilter: (filter) => {
    set({ schedulerWorkflowFilter: filter });
  },

  setSchedulerAssigneeFilter: (filter) => {
    set({ schedulerAssigneeFilter: filter });
  },

  toggleMyTasksForm: () => {
    set((state) => ({ myTasksFormOpen: !state.myTasksFormOpen }));
  },

  setMyTasksWorkflowFilter: (filter) => {
    set({ myTasksWorkflowFilter: filter });
  },

  setMyTasksStatusFilter: (status) => {
    set({ myTasksStatusFilter: status });
  },

  toggleSection: (section) => {
    set((state) => ({
      expandedSections: {
        ...state.expandedSections,
        [section]: !state.expandedSections[section],
      },
    }));
  },

  toggleWorkflow: (workflowInstanceId) => {
    set((state) => ({
      expandedWorkflows: {
        ...state.expandedWorkflows,
        [workflowInstanceId]: !state.expandedWorkflows[workflowInstanceId],
      },
    }));
  },

  resetFilters: () => {
    set({
      schedulerStatusFilter: 'ALL',
      schedulerWorkflowFilter: 'all',
      schedulerAssigneeFilter: 'all',
      myTasksWorkflowFilter: 'all',
      myTasksStatusFilter: 'ALL',
    });
  },
}));
